// Taşeron portalı — Supplier Self-Service durum/boş-durum ekranı (api-contract §13.6).
//
// Supplier **yalnız kendi** taşeron firmasını görür; contractor kimliği token/bağdan
// çözülür. İki özel durum nazikçe ele alınır (backend zorlar, FE açıklar):
//   - `404 CONTRACTOR_NOT_FOUND` → aktif firmada taşeron **bağı yok** ("firma ataması
//      bulunamadı"). Çok-firma supplier ise üstteki firma switcher'a yönlendirilir.
//   - `403 COMPANY_NOT_ACCESSIBLE` → aktif firmaya atama yok (cross-tenant kapısı).
//   - diğer (ağ/5xx) → genel hata + tekrar dene.
// Üç ekranın (firma/belge/personel) hepsi aynı bileşenle tutarlı durum gösterir.

import { Button, Card, EmptyState, isApiError } from '@isgportal/shared';
import { Building2, HardHat, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ContractorStateNoticeProps {
  error: unknown;
  /**
   * Başlık seviyesi. Varsayılan `h2` (ekranın `PageHeader` h1'i altında, S37 M5). Ekranda
   * h1 yoksa (ör. profil sayfasının hata dalı) çağıran `h1` verir.
   */
  headingAs?: 'h1' | 'h2';
  /** Genel (bilinmeyen) hatada yeniden denemek için. */
  onRetry?: () => void;
}

/** Hata kodunu ekrandaki içerik (ikon + başlık + gövde + tekrar) modeline eşler. */
function noticeFor(error: unknown): {
  icon: LucideIcon;
  titleKey: string;
  bodyKey: string;
  retryable: boolean;
} {
  const code = isApiError(error) ? error.errorCode : null;
  if (code === 'CONTRACTOR_NOT_FOUND') {
    return {
      icon: HardHat,
      titleKey: 'state.noContractor.title',
      bodyKey: 'state.noContractor.body',
      retryable: false,
    };
  }
  if (code === 'COMPANY_NOT_ACCESSIBLE') {
    return {
      icon: Building2,
      titleKey: 'state.noAccess.title',
      bodyKey: 'state.noAccess.body',
      retryable: false,
    };
  }
  return {
    icon: TriangleAlert,
    titleKey: 'state.error.title',
    bodyKey: 'state.error.body',
    retryable: true,
  };
}

export function ContractorStateNotice({
  error,
  onRetry,
  headingAs = 'h2',
}: ContractorStateNoticeProps) {
  const { t } = useTranslation();
  const notice = noticeFor(error);
  const Icon = notice.icon;

  return (
    <Card>
      <EmptyState
        icon={Icon}
        titleAs={headingAs}
        title={t(notice.titleKey)}
        description={t(notice.bodyKey)}
        action={
          notice.retryable && onRetry ? (
            <Button onClick={onRetry}>{t('state.error.retry')}</Button>
          ) : undefined
        }
      />
    </Card>
  );
}
